// @flow
/* eslint no-console:0 */
/* eslint import/prefer-default-export:0 */

import LogDataRateLimiter from 'app/common/redux/lib/LogDataRateLimiter';
import LogToServer from 'app/common/lib/LogToServer';

const RateLimiter = new LogDataRateLimiter(5000);

// for unit testing purposes we need an object to export some functions
// see https://github.com/facebook/jest/issues/936 for details
const functions = {};

export type LogDataType = {
  errorObj: {
    columnNumber?: string,
    fileName?: string,
    lineNumber?: string,
    message?: string,
    name?: string,
    stack?: string,
    moduleName?: string,
    location?: string,
  },
  auxData?: Object,
};
functions._reportToConsole = (error: Object, auxData?: Object = {}) => {
  if (console && console.error) {
    const {
      name = '',
      message = 'No message available',
      stack = 'No stack trace available',
    } = error;
    const errorMsg = `Name: ${name}\nMessage: ${message}\n----------\nStack: \n${stack}\n`;
    console.error(errorMsg, auxData);
  }
};

functions._reportToServer = (error: Object, auxData?: Object, serverLogType: string) => {
  const logData: LogDataType = {
    errorObj: error,
  };
  if (auxData) {
    logData.auxData = auxData;
  }

  const isResource = error && error.moduleName && error.moduleName === 'Resource';

  let numRetries;
  let retryDelay;
  let logToServerTime;

  if (isResource) {
    numRetries = 10;
    retryDelay = 10000;
    logToServerTime = Date.now();
  }

  if (!RateLimiter.isWithinRateLimit(logData)) {
    LogToServer.logToServer(
      serverLogType,
      LogToServer.HTTP_METHOD.POST,
      logData,
      numRetries,
      retryDelay,
      logToServerTime,
    );
  }
};

function _shouldReportError(error) {
  // Don't report 404/500 errors becuase they are not client side exceptions.
  // And those are logged on server side already.
  if (error.http_status === 404 || error.http_status === 500) {
    return false;
  }

  // Global code in the stack trace indicates that the error might have been
  // coming from code that is not ours.
  const stackTrace = error.stack || error.stack_trace;
  if (stackTrace && stackTrace.indexOf('global code') !== -1) {
    return false;
  }

  if (error.message) {
    if (
      error.message === 'Connectivity error or cancelled by navigation' ||
      // The following 2 erros only happen on Mobile Safari 7 or older and both
      // are not from our code.
      // - TypeError: undefined is not an object
      //   (evaluating 'document.getElementsByClassName('count mfss fcw')[1].innerHTML')"
      // - TypeError: null is not an object (evaluating 'elem.classList')
      // Ignore these error message for now because these are generating thousands of errors
      //  every second.
      error.message.includes('count mfss fcw') ||
      error.message.includes("evaluating 'elem.classList'")
    ) {
      return false;
    }
  }

  return true;
}

/**
 * Implementing a new function to report to node instead
 * of modifying the function above and the signature
 * of report handler on python. Function above will
 * be deprecated once node logging service is up.
 */
export const report = (
  error: Object,
  auxData?: Object,
  serverLogType: string = LogToServer.SERVER_LOG_TYPE.ERROR,
) => {
  const {
    columnNumber = '',
    fileName = '',
    lineNumber = '',
    message = '',
    original_message = '',
    name = '',
    stack = '',
    stack_trace = '',
    moduleName = '',
    location = '',
  } = error;

  // (TODO) add redux's store to payload later
  const payload = {
    columnNumber,
    fileName,
    lineNumber,
    message: original_message || message,
    name,
    stack: stack || stack_trace,
    moduleName,
    location,
  };
  functions._reportToConsole(payload, auxData);

  if (_shouldReportError(error)) {
    functions._reportToServer(payload, auxData, serverLogType);
  }
};

functions.report = report;
export default functions;
